// E.164 phone number validation regex
const isValidE164 = (phone: string) => {
  if (!phone) return true; // allow empty
  return /^\+?[1-9]\d{1,14}$/.test(phone);
};
import React, { useEffect, useState } from "react";
import ApiClient from "../services/fuelandmoreapi";
import { config } from "../config";
import type { TClientUser } from "@/types";
import BinIcon from "./BinIcon";
import UserForm from "./UserForm";
import "./UserList.css";

interface UserListProps {
  clientDomain: string;
  enableAddUser?: boolean; // New property to enable/disable Add User button
}

const UserList: React.FC<UserListProps> = ({
  clientDomain,
  enableAddUser = false,
}) => {
  const [users, setUsers] = useState<TClientUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // unified modal for add/edit
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<TClientUser | null>(null); // user being edited or added
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const apiClient = new ApiClient(apiUrl);
        const response = await apiClient.getClientUsers(
          undefined,
          clientDomain
        );
        setUsers(response.data);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [clientDomain, apiUrl]);

  // handleUserAdded removed, unified modal logic below

  const handleDeleteUser = async (email: string) => {
    await new ApiClient(apiUrl).deleteClientUser(email, clientDomain);
    setConfirmDelete(null);
    // Refresh users after deleting
    const response = await new ApiClient(apiUrl).getClientUsers(
      undefined,
      clientDomain
    );
    setUsers(response.data);
  };

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div className="user-list-container">
      <h3 className="user-list-title">Users for {clientDomain}</h3>

      <table className="user-list-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Client Domain</th>
            <th>Given Name</th>
            <th>Family Name</th>
            <th>Phone Number</th>
            <th>Job Title</th>
            <th>Company</th>
            <th>Country</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.email}>
              <td>{user.email}</td>
              <td>{user.clientDomain}</td>
              <td>{user.givenName}</td>
              <td>{user.familyName}</td>
              <td>{user.phoneNumber}</td>
              <td>{user.jobTitle}</td>
              <td>{user.company}</td>
              <td>{user.country}</td>
              <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                <button
                  className="edit-btn"
                  title="Edit User"
                  onClick={() => {
                    setEditUser(user);
                    setIsEditMode(true);
                    setShowModal(true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "black",
                    display: "inline-block",
                    margin: "0 auto",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 17.25V21h3.75l11.06-11.06a1 1 0 0 0 0-1.41l-2.24-2.24a1 1 0 0 0-1.41 0L3 17.25z"
                      stroke="black"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M14.06 7.94l2.24 2.24"
                      stroke="black"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </td>
              <td style={{ textAlign: "center" }}>
                <button
                  className="bin-btn"
                  title="Delete User"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(user.email);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <BinIcon color="black" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* UserForm modal for add/edit */}
      {showModal && editUser && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ width: "480px", maxWidth: "95%" }}
          >
            <h4>{isEditMode ? "Edit User" : "Add User"}</h4>
            <UserForm
              initialUser={editUser}
              mode={isEditMode ? "edit" : "add"}
              loading={editLoading}
              error={error || undefined}
              disableEmail={
                isEditMode &&
                editUser.email !== "" &&
                users.some((u) => u.email === editUser.email)
              }
              onSave={async (user) => {
                setEditLoading(true);
                setError(null);
                try {
                  const apiClient = new ApiClient(apiUrl);
                  if (!isEditMode) {
                    // Add
                    const newUser: TClientUser = {
                      ...user,
                      clientDomain,
                    };
                    await apiClient.createClientUser(newUser);
                    const updatedUsers = await apiClient.getClientUsers(
                      undefined,
                      clientDomain
                    );
                    setUsers(updatedUsers.data);
                    setShowModal(false);
                    setEditUser(null);
                  } else {
                    // Edit
                    const response = await apiClient.updateClientUser({
                      ...user,
                      clientDomain,
                    });
                    if (response.status < 200 || response.status >= 300) {
                      setError(
                        response.data?.message || "Failed to update user."
                      );
                    } else {
                      const updatedUsers = await apiClient.getClientUsers(
                        undefined,
                        clientDomain
                      );
                      setUsers(updatedUsers.data);
                      setShowModal(false);
                      setEditUser(null);
                    }
                  }
                } catch (err) {
                  setError(
                    !isEditMode
                      ? "Failed to add user."
                      : "Failed to update user."
                  );
                } finally {
                  setEditLoading(false);
                }
              }}
              onCancel={() => {
                setShowModal(false);
                setEditUser(null);
              }}
            />
          </div>
        </div>
      )}
      {error && <div className="user-list-error">{error}</div>}

      <div className="add-user-button-container">
        {enableAddUser && (
          <button
            className="primary-button"
            onClick={() => {
              setShowModal(true);
              setIsEditMode(false);
              setEditUser({
                email: "",
                givenName: "",
                familyName: "",
                phoneNumber: "",
                whatsAppId: "",
                jobTitle: "",
                company: "",
                country: "",
                appSource: undefined,
                clientDomain,
              });
            }}
          >
            + Add Client User
          </button>
        )}
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to delete the user <b>{confirmDelete}</b>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => handleDeleteUser(confirmDelete!)}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
